import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { usePeriod } from '@/app/PeriodContext';
import { PeriodBadge } from '@/app/PeriodBadge';
import { formatMoney, formatDate, isNegative } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { AccountForm } from './AccountForm';
import type { ReferenceData } from '@/features/transactions/types';

type Account = {
  id: number;
  name: string;
  type: string;
  entity_id: number;
  entity_name: string | null;
  entity_color: string | null;
  color: string | null;
  currency: string;
  bank_id: number | null;
  branch: string | null;
  holder_name: string | null;
  notes: string | null;
  balance: MoneyString;
  available_balance: MoneyString;
  reconciled_balance: MoneyString;
  opening_balance: MoneyString;
  opening_balance_date: string | null;
  overdraft_limit: MoneyString;
  blocked_amount: MoneyString;
  last_reconciled_at: string | null;
};

type StatementEntry = {
  source: 'baixa' | 'transferencia';
  source_id: number;
  date: string;
  description: string;
  category_name: string | null;
  counterpart: string | null;
  direction: 'entrada' | 'saida';
  amount: MoneyString;
  signed_amount: MoneyString;
  running_balance: MoneyString;
  is_reconciled: boolean;
  /**
   * Os identificadores para agir na linha — o backend já os entregava.
   *
   * `source_id` é o id da PERNA ou da BAIXA, e não serve para editar nem
   * excluir: a transferência tem duas pernas e a operação é sobre ela, não
   * sobre um lado. Por isso `transfer_id` vem separado.
   */
  transaction_id: number | null;
  transfer_id: number | null;
};

type Statement = {
  account: { id: number; name: string; type: string; currency: string };
  period: { from: string; to: string };
  opening_balance: MoneyString;
  closing_balance: MoneyString;
  total_in: MoneyString;
  total_out: MoneyString;
  entries: StatementEntry[];
};

export function AccountsPage({
  selectedIds,
  onOpenTransfers,
}: {
  selectedIds: number[];
  /** Leva para Transferências, onde a transferência se edita e se exclui. */
  onOpenTransfers?: () => void;
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Account | null>(null);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  const accounts = useQuery({
    queryKey: ['accounts', selectedIds],
    queryFn: () => api.get<{ data: Account[] }>('/accounts', { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/accounts/${id}`, { entityIds: selectedIds }),
    onSuccess: (res) => {
      // O backend encerra em vez de excluir quando há movimento. A mensagem
      // dele explica o que aconteceu — repassar é mais honesto que dizer
      // "excluída" quando a conta continua lá.
      window.alert(res.message);
      refresh();
    },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao excluir a conta.'),
  });

  function refresh() {
    setEditing(null);
    setCreating(false);
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['reference'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['statement'] });
  }

  if (accounts.isPending) return <Skeleton />;

  if (accounts.isError) {
    return (
      <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
        Não foi possível carregar as contas.
      </p>
    );
  }

  const total = accounts.data.reduce((acc, a) => acc + Number(a.balance), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Contas bancárias</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {accounts.data.length} conta(s) · saldo somado {formatMoney(total.toFixed(4))}
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          disabled={!reference.data}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
        >
          + Nova conta
        </button>
      </div>

      {accounts.data.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">
            Nenhuma conta nas entidades selecionadas.
          </p>
          <button
            onClick={() => setCreating(true)}
            disabled={!reference.data}
            className="mt-3 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            Cadastrar a primeira conta
          </button>
        </div>
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {accounts.data.map((a) => {
            return (
              <div
                key={a.id}
                className={`rounded-xl border p-4 transition ${
                  openId === a.id
                    ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30'
                    : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{a.name}</p>
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {a.entity_name} · {a.type}
                    </p>
                  </div>
                  <span
                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: a.color ?? a.entity_color ?? '#94a3b8' }}
                  />
                </div>

                <p
                  className={`mt-3 text-lg font-semibold ${
                    isNegative(a.balance) ? 'text-rose-600 dark:text-rose-400' : ''
                  }`}
                >
                  {formatMoney(a.balance)}
                </p>

                <dl className="mt-2 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {a.overdraft_limit !== '0.0000' && (
                    <div className="flex justify-between">
                      <dt>Disponível com limite</dt>
                      <dd>{formatMoney(a.available_balance)}</dd>
                    </div>
                  )}
                </dl>

                {/*
                  ================================================================
                  "CONCILIADO" SAIU DA TELA — RN-81
                  ================================================================

                  Mostrava "Conciliado R$ 0,00" e "R$ 600,00 não conciliado" em
                  TODA conta, sempre. O motivo: nao existe tela de conciliacao —
                  nenhuma rota, nenhum endpoint escreve `reconciled_at`.

                  O calculo esta certo e a regra existe. O que nao existe e o
                  caminho para conciliar, entao o numero ficaria eternamente em
                  zero e o "nao conciliado" repetiria o saldo inteiro.

                  Informar uma distincao sobre a qual ninguem pode agir nao
                  informa: gera a pergunta "o que e isso?" e nenhuma resposta
                  util. O usuario perguntou, e a resposta honesta era que nao
                  dava para fazer nada a respeito.

                  O CAMPO CONTINUA NO BANCO e o servico continua calculando. O
                  dia em que a conciliacao existir, isto volta — e volta
                  significando alguma coisa.
                */}

                <div className="mt-3 flex items-center gap-3 border-t border-slate-100 pt-2.5 text-[11px] dark:border-slate-800">
                  <button
                    onClick={() => setOpenId(openId === a.id ? null : a.id)}
                    className="text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    {openId === a.id ? '− fechar extrato' : '+ ver extrato'}
                  </button>
                  <button
                    onClick={() => setEditing(a)}
                    disabled={!reference.data}
                    className="text-slate-500 hover:underline disabled:opacity-50 dark:text-slate-400"
                  >
                    editar
                  </button>
                  <button
                    onClick={() => {
                      // RNF-25: confirmação antes de qualquer exclusão.
                      if (
                        window.confirm(
                          `Excluir a conta "${a.name}"?\n\n` +
                            'Se ela já tiver movimento, será encerrada em vez de excluída — ' +
                            'o extrato precisa continuar explicável.',
                        )
                      ) {
                        remove.mutate(a.id);
                      }
                    }}
                    className="ml-auto text-rose-600 hover:underline dark:text-rose-400"
                  >
                    excluir
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openId !== null && (
        <StatementPanel onOpenTransfers={onOpenTransfers} accountId={openId} selectedIds={selectedIds} />
      )}

      {(creating || editing) && reference.data && (
        <AccountForm
          account={editing}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function StatementPanel({
  accountId,
  selectedIds,
  onOpenTransfers,
}: {
  accountId: number;
  selectedIds: number[];
  onOpenTransfers?: () => void;
}) {
  const queryClient = useQueryClient();

  /**
   * Estornar uma baixa a partir do extrato.
   *
   * Estornar NÃO é excluir, e o texto do confirmar diz isso: a conta volta ao
   * saldo anterior e o título volta a ficar em aberto. Quem quer apagar o
   * lançamento faz isso em Lançamentos — são coisas diferentes, e confundi-las
   * some com uma despesa que continua devida.
   */
  async function reverseSettlement(e: StatementEntry) {
    if (
      !confirm(
        `Estornar esta baixa?

${e.description} · ${formatMoney(e.amount)}

` +
          'O valor volta para o saldo da conta e o título volta a ficar em aberto. ' +
          'O lançamento NÃO é excluído.',
      )
    ) {
      return;
    }

    try {
      await api.post(
        `/transactions/${e.transaction_id}/settlements/${e.source_id}/reverse`,
        { reversed_at: new Date().toISOString().slice(0, 10) },
        { entityIds: selectedIds },
      );

      void queryClient.invalidateQueries({ queryKey: ['statement'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['payables'] });
    } catch (error) {
      alert(error instanceof ApiError ? error.message : 'Não foi possível estornar.');
    }
  }

  // Componente aninhado lendo o período direto do contexto: é o caso que
  // justificou o contexto em vez de propriedade.
  const { period } = usePeriod();

  const statement = useQuery({
    queryKey: ['statement', accountId, period.from, period.to, selectedIds],
    queryFn: () =>
      api.get<{ data: Statement }>(
        `/accounts/${accountId}/statement?from=${period.from}&to=${period.to}`,
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
  });

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-3.5 dark:border-slate-800">
        <h2 className="font-medium">
          Extrato {statement.data ? `· ${statement.data.account.name}` : ''}
        </h2>
        <div className="ml-auto">
          <PeriodBadge />
        </div>
      </div>

      {statement.isPending ? (
        <div className="animate-pulse p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="mb-2 h-8 rounded bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      ) : statement.isError ? (
        <p className="p-8 text-center text-sm text-rose-600">Erro ao carregar o extrato.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-5 border-b border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm dark:border-slate-800 dark:bg-slate-800/40">
            <span>
              <span className="text-slate-500 dark:text-slate-400">Saldo anterior: </span>
              <strong>{formatMoney(statement.data.opening_balance)}</strong>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">Entradas: </span>
              <strong className="text-emerald-700 dark:text-emerald-400">
                {formatMoney(statement.data.total_in)}
              </strong>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">Saídas: </span>
              <strong className="text-rose-700 dark:text-rose-400">
                {formatMoney(statement.data.total_out)}
              </strong>
            </span>
            <span className="ml-auto">
              <span className="text-slate-500 dark:text-slate-400">Saldo final: </span>
              <strong>{formatMoney(statement.data.closing_balance)}</strong>
            </span>
          </div>

          {statement.data.entries.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhum movimento no período.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Data</th>
                    <th className="px-3 py-2 font-medium">Descrição</th>
                    <th className="px-3 py-2 font-medium">Origem</th>
                    <th className="px-3 py-2 text-right font-medium">Valor</th>
                    <th className="px-3 py-2 text-right font-medium">Saldo</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {statement.data.entries.map((e) => (
                    <tr
                      key={`${e.source}-${e.source_id}`}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="whitespace-nowrap px-3 py-2">{formatDate(e.date)}</td>
                      <td className="px-3 py-2">
                        <span className="font-medium">{e.description}</span>
                        {e.category_name && (
                          <span className="ml-1.5 text-xs text-slate-400">{e.category_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {/* Transferência tem cor própria — nunca verde ou
                            vermelho: ela não é receita nem despesa (RN-10). */}
                        <span
                          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                            e.source === 'transferencia'
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                          }`}
                        >
                          {e.source === 'transferencia' ? 'transferência' : 'lançamento'}
                        </span>
                        {/* O selo "não conciliado" saiu pelo mesmo motivo: sem
                            tela de conciliação, ele apareceria em toda linha do
                            extrato, para sempre. */}
                      </td>
                      <td
                        className={`whitespace-nowrap px-3 py-2 text-right font-medium ${
                          e.direction === 'entrada'
                            ? 'text-emerald-700 dark:text-emerald-400'
                            : 'text-rose-700 dark:text-rose-400'
                        }`}
                      >
                        {e.direction === 'entrada' ? '+' : '−'} {formatMoney(e.amount)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-slate-600 dark:text-slate-300">
                        {formatMoney(e.running_balance)}
                      </td>

                      {/*
                        ================================================================
                        AGIR ONDE SE VÊ
                        ================================================================

                        As ações existiam só na tela de Transferências. Mas é aqui que
                        a pessoa está quando descobre uma linha errada — ela abriu o
                        extrato justamente para entender um saldo.

                        A ação certa depende da ORIGEM, e a diferença não é cosmética:

                          transferência  editar / excluir a transferência inteira
                          lançamento     estornar a baixa (o lançamento continua devido)

                        Estornar não é excluir: a conta volta ao saldo anterior e o
                        título volta para "a pagar". Apagar o lançamento seria outra
                        coisa, e se faz em Lançamentos.
                      */}
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {e.source === 'transferencia' && e.transfer_id !== null ? (
                          <button
                            onClick={() => onOpenTransfers?.()}
                            className="rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-indigo-600"
                            title="Editar ou excluir esta transferência"
                          >
                            editar
                          </button>
                        ) : e.source === 'baixa' && e.transaction_id !== null ? (
                          <button
                            onClick={() => reverseSettlement(e)}
                            className="rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-rose-600"
                            title="Estornar a baixa — o título volta a ficar em aberto"
                          >
                            estornar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="border-t border-slate-200 px-3.5 py-2 text-[11px] text-slate-400 dark:border-slate-800">
            O extrato une baixas de lançamento e pernas de transferência — as duas únicas fontes de
            movimento de caixa do sistema.
          </p>
        </>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse">
      <div className="h-6 w-40 rounded bg-slate-200 dark:bg-slate-800" />
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-32 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
    </div>
  );
}
